/**
 * PowerPoint no formato editorial (vem do ManualMaker, pptx.js), com o kit de marca do
 * espaço: Capa (escura, coluna na cor da marca) → Sumário (com 5 ou mais passos) → um
 * diapositivo por passo ou bloco de texto → Fecho. Sem negrito; as contas de encaixe
 * estão em @nolens/domain (export/editorial.ts).
 */
import {
  displayUrl,
  editorialPalette,
  fitRect,
  fittedHeight,
  fitText,
  pad2,
  SLIDE,
  stepLayout,
  guideText,
  stepTitle,
  type Locale,
  TOC_MIN_STEPS,
  TOC_PER_COL,
  TOC_PER_SLIDE,
  type EditorialPalette,
  type FontChoice,
  type Step,
} from '@nolens/domain';
import type PptxGenJS from 'pptxgenjs';

type Slide = PptxGenJS.Slide;

export interface DeckImage {
  data: string;
  w: number;
  h: number;
}

export interface DeckInput {
  title: string;
  description: string;
  /** Língua do guia (textos do diapositivo e títulos automáticos). */
  language: Locale;
  steps: Step[];
  numbers: Map<string, number>;
  images: Map<string, DeckImage>;
  accent: string;
  coverColor: string | null;
  highlightColor: string | null;
  font: FontChoice | undefined;
  /** Nome no cabeçalho (o da empresa ou, sem ele, o do espaço). */
  brandName: string;
  logo: DeckImage | null;
  date: string;
}

const FONTS: Record<FontChoice, string> = {
  inter: 'Inter',
  system: 'Arial',
  serif: 'Georgia',
  mono: 'Consolas',
  custom: 'Inter',
};

const { w: W, h: H, mx: MX } = SLIDE;
const COL_X = 7.75;

export function buildDeck(pptx: PptxGenJS, d: DeckInput) {
  const L = d.language;
  const T = guideText(L);
  const upper = (s: string) => s.toLocaleUpperCase(L);
  const P: EditorialPalette = editorialPalette(d.accent, d.coverColor, d.highlightColor);
  const font = FONTS[d.font ?? 'inter'];
  const title = d.title.trim() || T.guide;
  const numbered = d.steps.filter((s) => s.kind !== 'text');
  const N = numbered.length;
  const tocSlides = N >= TOC_MIN_STEPS ? Math.ceil(N / TOC_PER_SLIDE) : 0;
  const total = 1 + tocSlides + d.steps.length + 1;
  const page = (n: number) => pad2(n) + ' / ' + pad2(total);
  const nPass = T.steps(N);

  const txt = (
    s: Slide,
    text: string,
    x: number,
    y: number,
    w: number,
    h: number,
    o: {
      pt: number;
      color: string;
      valign?: 'top' | 'middle' | 'bottom';
      align?: 'left' | 'right';
      spacing?: number;
    },
  ) =>
    s.addText(text, {
      x,
      y,
      w,
      h,
      fontFace: font,
      fontSize: o.pt,
      color: o.color,
      bold: false,
      margin: 0,
      valign: o.valign ?? 'top',
      align: o.align ?? 'left',
      ...(o.spacing ? { charSpacing: o.spacing } : {}),
      fit: 'none',
    });
  const rect = (s: Slide, x: number, y: number, w: number, h: number, color: string) =>
    s.addShape('rect', { x, y, w, h, fill: { color }, line: { color, width: 0 } });

  // Cabeçalho em maiúsculas pequenas, em colunas: marca · GUIA · título · página
  const header = (s: Slide, color: string, docTitle: string, pg: string, right = 9.5) => {
    const o = { pt: 7.5, color, spacing: 0.8 };
    txt(s, upper(d.brandName).slice(0, 40), MX, 0.3, 1.8, 0.18, o);
    txt(s, 'GUIA', 2.35, 0.3, 1.4, 0.18, o);
    if (docTitle) {
      let label = upper(docTitle);
      if (label.length > 44) label = label.slice(0, 43).trimEnd() + '…';
      txt(s, label, 3.85, 0.3, Math.min(4.0, right - 3.95), 0.18, o);
    }
    if (pg) txt(s, pg, right - 1.4, 0.3, 1.4, 0.18, { ...o, align: 'right' });
  };

  const darkWithColumn = (s: Slide) => {
    s.background = { color: P.dark };
    header(s, P.onDark, '', '', COL_X - 0.3);
    rect(s, COL_X, 0, W - COL_X, H, P.column);
    if (d.logo) {
      const r = fitRect(d.logo.w, d.logo.h, COL_X + 0.3, 0.3, W - COL_X - 0.6, 0.55);
      s.addImage({ data: d.logo.data, x: r.x, y: 0.3, w: r.w, h: r.h, altText: d.brandName });
    }
  };

  // ── Capa ─────────────────────────────────────────────────────────────────
  {
    const s = pptx.addSlide();
    darkWithColumn(s);
    txt(s, pad2(N), COL_X + 0.3, 3.55, W - COL_X - 0.6, 0.85, {
      pt: 54,
      color: P.onColumn,
      valign: 'bottom',
    });
    txt(s, N === 1 ? 'PASSO' : 'PASSOS', COL_X + 0.3, 4.5, W - COL_X - 0.6, 0.2, {
      pt: 8,
      color: P.onColumn,
      spacing: 1,
    });
    const t = fitText(title, COL_X - MX - 0.45, 2.1, 44, 26);
    txt(s, t.text, MX, 1.55, COL_X - MX - 0.45, 2.1, {
      pt: t.pt,
      color: P.onDark,
      valign: 'bottom',
    });
    rect(s, MX, 3.9, 0.6, 0.045, P.highlight);
    txt(s, d.date, MX, 4.08, 4, 0.3, { pt: 12, color: P.highlight });
    if (d.description.trim()) {
      const desc = fitText(d.description, COL_X - MX - 0.45, 0.7, 11, 9);
      txt(s, desc.text, MX, 4.5, COL_X - MX - 0.45, 0.7, { pt: desc.pt, color: P.onDark });
    }
  }

  // ── Sumário (≥ 5 passos) ─────────────────────────────────────────────────
  const labels = numbered.map((s) => stepTitle(s, L));
  for (let k = 0; k < tocSlides; k++) {
    const s = pptx.addSlide();
    s.background = { color: P.paper };
    header(s, P.ink, title, page(2 + k));
    txt(s, T.contents, MX, 0.72, 2.6, 0.5, { pt: 26, color: P.ink });
    rect(s, MX, 1.38, 0.6, 0.045, P.column);
    txt(s, nPass, MX, 1.55, 2.6, 0.3, { pt: 11, color: P.ink });
    const colW = 2.85;
    for (let c = 0; c < 2; c++) {
      const start = k * TOC_PER_SLIDE + c * TOC_PER_COL;
      const items = labels.slice(start, start + TOC_PER_COL);
      if (!items.length) break;
      const x = 3.6 + c * (colW + 0.2);
      const list = (texts: string[], color: string) =>
        texts.map((t, j) => ({
          text: t,
          options: {
            color,
            fontSize: 11,
            fontFace: font,
            breakLine: j < texts.length - 1,
            paraSpaceAfter: 7,
          },
        }));
      const nums = items.map((_, j) => pad2(start + j + 1));
      const names = items.map((l) => fitText(l, colW - 0.45, (11 * 1.32) / 72 + 0.01, 11, 11).text);
      s.addText(list(nums, P.number), {
        x,
        y: 0.74,
        w: 0.4,
        h: 4.5,
        margin: 0,
        valign: 'top',
        fit: 'none',
      });
      s.addText(list(names, P.ink), {
        x: x + 0.45,
        y: 0.74,
        w: colW - 0.45,
        h: 4.5,
        margin: 0,
        valign: 'top',
        fit: 'none',
      });
    }
  }

  // ── Passos e blocos de texto ─────────────────────────────────────────────
  d.steps.forEach((st, i) => {
    const s = pptx.addSlide();
    s.background = { color: P.paper };
    const pg = page(2 + tocSlides + i);
    header(s, P.ink, title, pg);

    if (st.kind === 'text') {
      const tone = st.tone || 'info';
      const head = st.title.trim() || T[tone];
      if (tone === 'heading') {
        const t = fitText(head, 8.5, 1.6, 36, 22);
        txt(s, t.text, MX, 1.2, 8.5, 1.6, { pt: t.pt, color: P.ink, valign: 'bottom' });
        rect(s, MX, 2.95, 0.6, 0.045, P.column);
        if (st.description.trim()) {
          const b = fitText(st.description, 7, 1.9, 14, 10);
          txt(s, b.text, MX, 3.15, 7, 1.9, { pt: b.pt, color: P.ink });
        }
      } else {
        txt(s, upper(T[tone]), MX, 1.0, 4, 0.25, { pt: 9, color: P.number, spacing: 1 });
        rect(s, MX, 1.32, 0.36, 0.035, P.column);
        const t = fitText(head, 8.5, 1.1, 26, 16);
        txt(s, t.text, MX, 1.5, 8.5, 1.1, { pt: t.pt, color: P.ink });
        if (st.description.trim()) {
          const y = 1.5 + fittedHeight(t) + 0.2;
          const b = fitText(st.description, 8.5, 5.1 - y, 15, 10);
          txt(s, b.text, MX, y, 8.5, 5.1 - y, { pt: b.pt, color: P.ink });
        }
      }
      if (st.description.trim()) s.addNotes(st.description.trim());
      return;
    }

    const num = pad2(d.numbers.get(st.id) ?? i + 1);
    const t0 = stepTitle(st, L);
    // Num passo «Abrir», o endereço (se o título ainda não o diz)
    const url = (st.kind === 'navigate' || st.kind === 'tab') && st.url ? displayUrl(st.url) : '';
    const desc = st.description.trim() || (url && !t0.includes(url) ? url : '');
    const img = d.images.get(st.id) ?? null;
    const frame = (r: { x: number; y: number; w: number; h: number }) => {
      s.addImage({ data: img!.data, ...r, altText: t0 });
      s.addShape('rect', {
        ...r,
        fill: { color: P.ink, transparency: 100 },
        line: { color: P.ink, width: 0.75, transparency: 82 },
      });
    };

    if (stepLayout(img ? img.w / img.h : null, t0, desc) === 'wide') {
      const t = fitText(t0, 7.95, 0.42, 20, 15);
      txt(s, num, MX, 0.6, 1.0, 0.72, { pt: 36, color: P.number });
      rect(s, MX, 1.36, 0.36, 0.035, P.column);
      txt(s, t.text, 1.55, 0.66, 7.95, 0.42, { pt: t.pt, color: P.ink });
      if (desc) {
        const dd = fitText(desc, 7.95, 0.42, 11, 10);
        txt(s, dd.text, 1.55, 1.1, 7.95, 0.42, { pt: dd.pt, color: P.ink });
      }
      frame(fitRect(img!.w, img!.h, MX, 1.62, 9.0, 3.63));
    } else {
      const colW = img ? 3.05 : 8.5;
      const t = fitText(t0, colW, 1.35, 22, 14);
      const tY = 1.72;
      const dY = tY + fittedHeight(t) + 0.18;
      txt(s, num, MX, 0.6, 1.4, 0.9, { pt: 48, color: P.number });
      rect(s, MX, 1.52, 0.36, 0.035, P.column);
      txt(s, t.text, MX, tY, colW, 1.35, { pt: t.pt, color: P.ink });
      if (desc && dY < 5.0) {
        const dd = fitText(desc, colW, 5.2 - dY, 12, 10);
        txt(s, dd.text, MX, dY, colW, 5.2 - dY, { pt: dd.pt, color: P.ink });
      }
      if (img) frame(fitRect(img.w, img.h, 4.0, 0.62, 5.5, 4.6));
    }
    s.addNotes([t0, st.description.trim()].filter(Boolean).join('\n\n'));
  });

  // ── Fecho ────────────────────────────────────────────────────────────────
  {
    const s = pptx.addSlide();
    darkWithColumn(s);
    txt(s, T.endOfGuide, MX, 1.6, COL_X - MX - 0.45, 1.0, {
      pt: 40,
      color: P.onDark,
      valign: 'bottom',
    });
    rect(s, MX, 2.85, 0.6, 0.045, P.highlight);
    const t = fitText(title, COL_X - MX - 0.45, 0.9, 14, 11);
    txt(s, t.text, MX, 3.03, COL_X - MX - 0.45, 0.9, { pt: t.pt, color: P.onDark });
    txt(s, nPass + '  ·  ' + d.date, MX, 3.03 + fittedHeight(t) + 0.1, 4, 0.3, {
      pt: 11,
      color: P.highlight,
    });
  }
}
